import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BookOpen, ExternalLink, ChevronRight, ArrowLeft,
  Clock, AlertTriangle, Info, Stethoscope,
} from '../components/Icons';
import { Apple, Lightbulb, Baby, Pill, AlertCircle, ChartColumn } from 'lucide-react';
import { ARTICLES, CATEGORIES } from './articleData';
import styles from './InfoHub.module.css';

// peta iconKey (string di data) → komponen ikon
const ICONS = {
  link: ExternalLink,
  apple: Apple,
  chart: ChartColumn,
  bulb: Lightbulb,
  baby: Baby,
  pill: Pill,
  alert: AlertCircle,
};

function renderIcon(key, size = 24) {
  const Cmp = ICONS[key] || Info;
  return <Cmp size={size} strokeWidth={2} aria-hidden="true" />;
}

// parser konten: ubah teks **judul**, "- butir", paragraf → elemen rapi.
// Butir berurutan dikelompokkan ke dalam satu <ul> (aksesibilitas).
function renderContent(text, styles) {
  const lines = text.split('\n');
  const blocks = [];
  let listBuffer = [];

  const flushList = (key) => {
    if (listBuffer.length) {
      blocks.push(
        <ul key={`ul-${key}`} className={styles.contentList}>
          {listBuffer.map((item, i) => (
            <li key={i} className={styles.contentLi}>{item}</li>
          ))}
        </ul>
      );
      listBuffer = [];
    }
  };

  lines.forEach((raw, i) => {
    const line = raw.trim();
    if (line.startsWith('**') && line.endsWith('**')) {
      flushList(i);
      blocks.push(
        <h3 key={i} className={styles.contentHeading}>{line.replace(/\*\*/g, '')}</h3>
      );
    } else if (line.startsWith('- ')) {
      listBuffer.push(line.slice(2));
    } else if (line === '') {
      flushList(i);
    } else {
      flushList(i);
      // tebalkan penanda **...** di tengah baris (mis. "**Tipe 1** — ...")
      const parts = line.split(/(\*\*[^*]+\*\*)/g).filter(Boolean);
      blocks.push(
        <p key={i} className={styles.contentP}>
          {parts.map((p, j) =>
            p.startsWith('**') && p.endsWith('**')
              ? <strong key={j}>{p.replace(/\*\*/g, '')}</strong>
              : p
          )}
        </p>
      );
    }
  });
  flushList('end');
  return blocks;
}

function InfoHub() {
  const { i18n } = useTranslation();
  const isID = i18n.language === 'id';
  const [activeCategory, setActiveCategory] = useState('Semua');
  const [selected, setSelected] = useState(null);

  const filtered = activeCategory === 'Semua'
    ? ARTICLES
    : ARTICLES.filter(a => a.category === activeCategory);

  // ===== HALAMAN DETAIL =====
  if (selected) {
    const content = isID ? selected.content_id : selected.content_en;
    return (
      <div className={styles.page}>
        <div className={styles.detail}>
          <button className={styles.backBtn} onClick={() => setSelected(null)}>
            <ArrowLeft size={16} aria-hidden="true" />
            {isID ? 'Kembali ke daftar' : 'Back to list'}
          </button>

          <article className={styles.article}>
            <div className={styles.articleHead}>
              <div className={styles.articleIcon}>{renderIcon(selected.iconKey, 28)}</div>
              <div className={styles.articleMeta}>
                <span className={styles.articleCategory}>{selected.category}</span>
                <span className={styles.readTime}>
                  <Clock size={13} aria-hidden="true" />
                  {selected.readMinutes} {isID ? 'menit baca' : 'min read'}
                </span>
                {selected.medicalReview && (
                  <span className={styles.reviewBadge}>
                    <Stethoscope size={13} aria-hidden="true" />
                    {isID ? 'Perlu tinjauan medis' : 'Pending medical review'}
                  </span>
                )}
              </div>
              <h1 className={styles.articleTitle}>
                {isID ? selected.title_id : selected.title_en}
              </h1>
            </div>

            <div className={styles.content}>
              {renderContent(content, styles)}
            </div>

            {/* Rujukan */}
            {selected.references?.length > 0 && (
              <div className={styles.refs}>
                <div className={styles.refsTitle}>
                  <BookOpen size={15} aria-hidden="true" />
                  {isID ? 'Rujukan Ilmiah' : 'Scientific References'}
                </div>
                <ul className={styles.refsList}>
                  {selected.references.map((ref, i) => (
                    <li key={i} className={styles.refItem}>
                      <span aria-hidden="true">{i + 1}.</span>
                      <a className={styles.refLink} href={ref.url}
                        target="_blank" rel="noreferrer">
                        {ref.label}
                        <ExternalLink size={12} aria-hidden="true" />
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Disclaimer */}
            <div className={styles.disclaimer}>
              <Info size={16} aria-hidden="true" />
              <span>
                {isID
                  ? 'Informasi ini bersifat edukatif dan bukan pengganti diagnosis atau nasihat medis profesional. Selalu konsultasikan kondisi anak Anda dengan dokter.'
                  : 'This information is educational and not a substitute for professional medical diagnosis or advice. Always consult your child\u2019s condition with a doctor.'}
              </span>
            </div>
          </article>
        </div>
      </div>
    );
  }

  // ===== DAFTAR ARTIKEL =====
  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}>
          <BookOpen size={30} strokeWidth={2.2} aria-hidden="true" />
          {isID ? 'Pusat Informasi' : 'Information Hub'}
        </h1>
        <p className={styles.subtitle}>
          {isID
            ? 'Pelajari informasi terpercaya seputar kesehatan pencernaan (GI) dan ASD untuk mendukung perawatan anak Anda.'
            : 'Learn trusted information about GI health and ASD to support your child\u2019s care.'}
        </p>
      </div>

      {/* Filter */}
      <div className={styles.filterRow} role="group"
        aria-label={isID ? 'Filter kategori' : 'Category filter'}>
        {CATEGORIES.map(cat => (
          <button key={cat}
            className={`${styles.filterBtn} ${activeCategory === cat ? styles.filterBtnActive : ''}`}
            aria-pressed={activeCategory === cat}
            onClick={() => setActiveCategory(cat)}>
            {cat === 'Semua' ? (isID ? 'Semua' : 'All') : cat}
          </button>
        ))}
      </div>

      {/* Grid */}
      <div className={styles.grid}>
        {filtered.map(article => (
          <button key={article.id} className={styles.card}
            onClick={() => setSelected(article)}>
            <div className={styles.cardIconRow}>
              <div className={styles.cardIcon}>{renderIcon(article.iconKey, 24)}</div>
              <span className={styles.readTime}>
                <Clock size={13} aria-hidden="true" />
                {article.readMinutes} {isID ? 'mnt' : 'min'}
              </span>
            </div>
            <div className={styles.cardCategory}>{article.category}</div>
            <h2 className={styles.cardTitle}>
              {isID ? article.title_id : article.title_en}
            </h2>
            <p className={styles.cardSummary}>
              {isID ? article.summary_id : article.summary_en}
            </p>
            <span className={styles.cardMore}>
              {isID ? 'Baca selengkapnya' : 'Read more'}
              <ChevronRight size={16} strokeWidth={2} aria-hidden="true" />
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

export default InfoHub;